/**
 * SessionTracker Component
 *
 * Tracks login, logoff, and idle timeout events with user context.
 * Captures: username, customer name, DC/site, device info.
 * Session duration is calculated at query time in Athena by joining
 * SESSION_START and SESSION_END events on the same session_id.
 *
 * Requirements: 1 (Login datetime), 2 (Logoff datetime), 3 (Session duration via Athena)
 */

import { useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import { Auth } from 'aws-amplify';
import { useTracking } from '@rehrig/tracking-sdk';
import { setTrackSessionEndFn, setFlushTrackingFn } from '../../utils/trackingUtils';

// Idle timeout: 15 minutes of inactivity triggers auto-logoff tracking
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function SessionTracker({ authStatus }) {
  const { trackEvent, isEnabled, flushTracking, forceTrackEvent } = useTracking();
  const sessionEndSentRef = useRef(false);
  const prevAuthStatusRef = useRef(authStatus);

  // Pull user context from Redux
  const selectedCustomer = useSelector((state) => state.Routes?.selectedCustomerListData);
  const selectedLocation = useSelector((state) => state.Routes?.selectedLocationListData);

  // Store enrichment in refs so it's available during tab_close/beforeunload teardown
  const customerNameRef = useRef(null);
  const locationNameRef = useRef(null);

  // Keep refs in sync with Redux state
  useEffect(() => {
    customerNameRef.current = selectedCustomer?.customerName || null;
  }, [selectedCustomer]);

  useEffect(() => {
    locationNameRef.current = selectedLocation?.locationName || null;
  }, [selectedLocation]);

  /**
   * Get current user details from Cognito
   */
  const getUserContext = async () => {
    let username = null;
    try {
      const user = await Auth.currentUserInfo();
      username = user?.attributes?.email
        || user?.attributes?.preferred_username
        || user?.username
        || null;
    } catch {
      // Not authenticated or failed - use null
    }

    return {
      environment: process.env.REACT_APP_ENVIRONMENT || 'dev',
      ...(username ? { user_id_hash: username } : {}),
      // Include customer/location from refs - available even during tab_close teardown
      ...(customerNameRef.current ? { customer_name: customerNameRef.current } : {}),
      ...(locationNameRef.current ? { location_name: locationNameRef.current } : {}),
    };
  };

  /**
   * Get device info for SESSION_START event
   */
  const getDeviceInfo = () => ({
    user_agent: navigator.userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const getSessionId = () =>
    sessionStorage.getItem('tracking_session_id') || `session_${Date.now()}`;

  /**
   * Fire SESSION_START event - records login time, username, customer, DC
   */
  const trackSessionStart = async () => {
    if (!isEnabled) return;
    sessionEndSentRef.current = false;

    const userContext = await getUserContext();

    trackEvent({
      event_type: 'SESSION_START',
      timestamp: new Date().toISOString(),
      session_id: getSessionId(),
      app_name: 'iris-portal',
      user_context: userContext,
      device_info: getDeviceInfo(),
    });

    console.log('[SessionTracker] SESSION_START tracked', {
      username: userContext.user_id_hash,
      customer: selectedCustomer?.customerName,
      location: selectedLocation?.locationName,
    });
  };

  /**
   * Fire SESSION_END event - records logoff time and reason.
   * Session duration = SESSION_END.timestamp - SESSION_START.timestamp
   * calculated at query time in Athena using session_id join.
   *
   * @param {string} reason - 'logout' | 'idle' | 'tab_close'
   */
  const trackSessionEnd = async (reason) => {
    if (sessionEndSentRef.current) return; // Prevent duplicate events
    sessionEndSentRef.current = true;

    console.log('[SessionTracker] trackSessionEnd called, reason:', reason);
    const userContext = await getUserContext();
    console.log('[SessionTracker] userContext:', userContext);

    // Use forceTrackEvent to bypass isEnabled check - SESSION_END must be sent
    // even if tracking is being disabled as part of the logout flow
    await forceTrackEvent({
      event_type: 'SESSION_END',
      timestamp: new Date().toISOString(),
      session_id: getSessionId(),
      app_name: 'iris-portal',
      user_context: userContext,
      metadata: { reason },
    });

    console.log(`[SessionTracker] SESSION_END tracked - reason: ${reason}`);

    // Clear the stored session id AFTER the SESSION_END event has been emitted,
    // so the next SESSION_START (e.g. after idle re-activity or re-login) gets a fresh id.
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('tracking_session_id');
    }
  };

  // Register global tracking functions for use outside React tree (e.g. logout button)
  useEffect(() => {
    setTrackSessionEndFn(trackSessionEnd);
    setFlushTrackingFn(flushTracking);
  }, [flushTracking]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track SESSION_START when user authenticates AND tracking is enabled (userData loaded)
  // isEnabled is included so SESSION_START re-fires once userData is available
  useEffect(() => {
    if (authStatus === 'authenticated' && isEnabled) {
      trackSessionStart();
    }
  }, [authStatus, isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track SESSION_END when user explicitly logs out
  useEffect(() => {
    const prev = prevAuthStatusRef.current;
    prevAuthStatusRef.current = authStatus;

    if (prev === 'authenticated' && authStatus === 'unauthenticated') {
      trackSessionEnd('logout');
    }
  }, [authStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track SESSION_END on tab/browser close
  useEffect(() => {
    const handleBeforeUnload = () => trackSessionEnd('tab_close');
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track SESSION_END after 15 minutes of inactivity
  useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle: () => {
      console.log('[SessionTracker] User went idle - tracking session end');
      trackSessionEnd('idle');
    },
    debounce: 500,
  });

  return null;
}

export default SessionTracker;
